"use client";

import { useCallback, useRef, useState } from "react";

interface UploadParams {
  base64: string;
  fileName: string;
  fileType?: string;
  storageCode: string;
  apiUrl: string;
  token: string;
}

interface StorageFileResponse {
  id: string;
  name: string | null;
  bucketId: string | null;
  userId: string | null;
  createdAt: string;
  updatedAt: string;
  downloadLink: string | null;
  download_link: string | null;
  downloadFileLink: string | null;
  download_file_link: string | null;
}

// Chunk settings matching Angular's Resumable.js config:
// chunkSize: 1 * 512 * 512, simultaneousUploads: 3
const CHUNK_SIZE = 1 * 512 * 512; // 512KB
const SIMULTANEOUS_UPLOADS = 3;

export function useStorageUpload() {
  const [isUploading, setIsUploading] = useState(false);
  // null = tidak sedang upload; 0-100 = persentase chunk yang sudah terupload
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const uploadedFileIdRef = useRef<string | null>(null);

  const upload = useCallback(async (params: UploadParams): Promise<{ fileId: string; downloadLink: string | null; name: string | null } | null> => {
    const { base64, fileName, fileType, storageCode, apiUrl, token } = params;
    setIsUploading(true);
    setProgress(0);
    setError(null);

    try {
      const storageEndpoint = `${apiUrl}/storage/v1/`;
      const authHeaders: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      };

      // Step 1: Initialize file upload
      // Angular: this.service.initialize(storage, { name: file.name })
      // → POST storages/{storage}/files/init
      const initResponse = await fetch(
        `${storageEndpoint}storages/${storageCode}/files/init`,
        {
          method: "POST",
          headers: {
            ...authHeaders,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name: fileName }),
          cache: "no-store",
          next: { revalidate: 0 },
        },
      );

      if (!initResponse.ok) {
        throw new Error("Gagal menginisialisasi upload file.");
      }

      const initData = await initResponse.json();
      const fileId = initData?.data?.id || initData?.id;

      if (!fileId) {
        throw new Error("ID file tidak ditemukan dari server.");
      }

      // Step 2: Convert base64 to File
      const blobResponse = await fetch(base64);
      const blob = await blobResponse.blob();
      const file = new File([blob], fileName, { type: fileType || blob.type || "application/octet-stream" });

      // Step 3: Upload file in chunks (matching Resumable.js protocol)
      // Angular: new Resumable({ target: getUploadEndpoint(storage), chunkSize, simultaneousUploads, ... })
      // → POST storages/{storage}/files/upload
      const uploadUrl = `${storageEndpoint}storages/${storageCode}/files/upload`;
      const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      let uploadedChunks = 0;

      const uploadChunk = async (chunkNumber: number): Promise<boolean> => {
        const start = (chunkNumber - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append("file", chunk, fileName);

        // Resumable.js parameters
        formData.append("resumableChunkNumber", String(chunkNumber));
        formData.append("resumableTotalChunks", String(totalChunks));
        formData.append("resumableChunkSize", String(CHUNK_SIZE));
        formData.append("resumableTotalSize", String(file.size));
        formData.append("resumableIdentifier", String(fileId));
        formData.append("resumableFilename", fileName);
        formData.append("resumableRelativePath", fileName);
        formData.append("resumableType", file.type || "application/octet-stream");
        formData.append("resumableCurrentChunkSize", String(end - start));

        const chunkResponse = await fetch(uploadUrl, {
          method: "POST",
          headers: authHeaders,
          body: formData,
          cache: "no-store",
          next: { revalidate: 0 },
        });

        if (!chunkResponse.ok) {
          throw new Error(`Gagal mengupload chunk ${chunkNumber}/${totalChunks}.`);
        }

        // Progress bertambah setiap chunk selesai terupload
        uploadedChunks += 1;
        setProgress(Math.round((uploadedChunks / totalChunks) * 100));

        return true;
      };

      // Upload chunks with limited concurrency (matching simultaneousUploads: 3)
      const chunkNumbers = Array.from({ length: totalChunks }, (_, i) => i + 1);
      const queue = [...chunkNumbers];
      const active: Promise<boolean>[] = [];

      const processQueue = async (): Promise<void> => {
        while (queue.length > 0) {
          const chunkNumber = queue.shift()!;
          await uploadChunk(chunkNumber);
        }
      };

      // Start up to SIMULTANEOUS_UPLOADS workers
      const workers = Array.from(
        { length: Math.min(SIMULTANEOUS_UPLOADS, totalChunks) },
        () => processQueue(),
      );

      await Promise.all(workers);

      // Step 4: Complete the upload
      // Angular: this.service.complete(storage, result.file)
      // → PATCH storages/{storage}/files/{file}/complete
      const completeResponse = await fetch(
        `${storageEndpoint}storages/${storageCode}/files/${fileId}/complete`,
        {
          method: "PATCH",
          headers: {
            ...authHeaders,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
          cache: "no-store",
          next: { revalidate: 0 },
        },
      );

      if (!completeResponse.ok) {
        throw new Error("Gagal menyelesaikan upload file.");
      }

      const completeData: StorageFileResponse = await completeResponse.json().then(
        (res) => res?.data || res,
      );

      const finalFileId = completeData?.id || fileId;
      const finalDownloadLink =
        completeData?.downloadLink ||
        completeData?.download_link ||
        completeData?.downloadFileLink ||
        completeData?.download_file_link ||
        null;
      uploadedFileIdRef.current = finalFileId;
      setIsUploading(false);
      setProgress(null); // progress bar hilang setelah upload selesai
      return { fileId: finalFileId, downloadLink: finalDownloadLink, name: completeData?.name || fileName };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Terjadi kesalahan saat upload.";
      setError(message);
      setIsUploading(false);
      setProgress(null);
      return null;
    }
  }, []);

  return { upload, isUploading, progress, error };
}
